/**
 Project	IAM - Validation

 @file 	    orgs.service.ts
 @author  	Martin Tazlari <martin.tazlari@ictvs.ch>
 @copyright © 2018, ICT-vs. All rights reserved.
 **/

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserInfo } from 'src/app/models/user-info.model';
import { MultiOrgResponse } from 'src/app/models/services/orgs/org-service-response.model';
import { LocalStorageService } from '../localStorage/localStorage.service';
import { OrgModel } from 'src/app/models/org.model';
import { MultiUserResponse } from 'src/app/models/services/users/user-service-response.model';
import { LastUpdateResponse } from 'src/app/models/services/last-update-response.model';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class OrgsService {
  public currentUserSubject: BehaviorSubject<UserInfo>;
  public currentUser: Observable<UserInfo>;
  public usermail: string;
  public userFirstname: string;
  public userLastname: string;

  constructor(
    private http: HttpClient,
    private _localStorageService: LocalStorageService
  ) {}

  /**
   * Orgs's Members
   * @param orgOid
   * @returns
   */
  getMembers(orgOid: string): Observable<UserInfo[]> {
    return this.http
      .get<MultiUserResponse>(
        `${environment.apiUrl}/orgs/${encodeURIComponent(orgOid)}/members`,
        httpOptions
      )
      .pipe(
        map(response => {
          return response.users;
        })
      );
  }

  /**
   * Get assignable orgs for a user
   * @param groupEtablissement
   * @returns
   */
  getMyAssignableRole(groupEtablissement: string): Observable<OrgModel[]> {
    let params = new HttpParams();
    params = params.append('groupeEtablissementName', groupEtablissement);
    return this.http
      .get<MultiOrgResponse>(environment.apiUrl + '/orgs/assignable', {
        params: params,
        headers: httpOptions.headers,
      })
      .pipe(
        map(response => {
          this._localStorageService.setItem('assignableOrgs', response.orgs);
          return response.orgs;
        })
      );
  }

  /**
   * Get (my) orgs by group etablissement
   * @param groupEtablissement
   * @returns
   */
  getMyOrgs(groupEtablissement: string): Observable<OrgModel[]> {
    return this.http
      .post<MultiOrgResponse>(
        environment.apiUrl +
          '/orgs/my?name=' +
          encodeURIComponent(groupEtablissement),
        httpOptions
      )
      .pipe(map(response => response.orgs));
  }

  /**
   * Get the cache last updated datetime as a string
   * @returns
   */
  getOrgsCacheUpdateDate(): Observable<string> {
    return this.http
      .get<LastUpdateResponse>(environment.apiUrl + '/orgs/lastupdate', {})
      .pipe(
        map(response => {
          return response.lastupdate;
        })
      );
  }

  /**
   * Force the API to load or update the orgs cache
   * @returns An observable with an empty response
   */
  refreshOrgsCache(): Observable<void> {
    return this.http.get<void>(environment.apiUrl + '/orgs/populate', {});
  }
}
